import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface ScorecardRunStatusCountsData {
  passedRows?: number;
  failedRows?: number;
  warnedRows?: number;
  runningRows?: number;
  queuedRows?: number;
}

interface ScorecardRunStatusCountsProps {
  counts: ScorecardRunStatusCountsData;
  className?: string;
}

interface StatusItem {
  key: string;
  node: ReactNode;
}

const StatusDot = ({ className }: { className: string }) => (
  <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", className)} aria-hidden />
);

/**
 * Compact live breakdown under a running progress bar
 * (e.g. "● 32 passed · ● 3 failed · ● 1 running · ● 14 queued").
 */
const ScorecardRunStatusCounts = ({ counts, className }: ScorecardRunStatusCountsProps) => {
  const items: StatusItem[] = [];

  if (typeof counts.passedRows === "number") {
    items.push({
      key: "passed",
      node: (
        <span className="inline-flex items-center gap-1.5 tabular-nums text-foreground">
          <StatusDot className="bg-emerald-500" />
          {counts.passedRows.toLocaleString()} passed
        </span>
      ),
    });
  }

  if (typeof counts.failedRows === "number" && counts.failedRows > 0) {
    items.push({
      key: "failed",
      node: (
        <span className="inline-flex items-center gap-1.5 tabular-nums text-foreground">
          <StatusDot className="bg-red-500" />
          {counts.failedRows.toLocaleString()} failed
        </span>
      ),
    });
  }

  if (typeof counts.warnedRows === "number" && counts.warnedRows > 0) {
    items.push({
      key: "warned",
      node: (
        <span className="inline-flex items-center gap-1.5 tabular-nums text-foreground">
          <StatusDot className="bg-amber-500" />
          {counts.warnedRows.toLocaleString()} warned
        </span>
      ),
    });
  }

  if (typeof counts.runningRows === "number" && counts.runningRows > 0) {
    items.push({
      key: "running",
      node: (
        <span className="inline-flex items-center gap-1.5 tabular-nums text-foreground">
          <StatusDot className="bg-blue-500" />
          {counts.runningRows.toLocaleString()} running
        </span>
      ),
    });
  }

  if (typeof counts.queuedRows === "number" && counts.queuedRows > 0) {
    items.push({
      key: "queued",
      node: (
        <span className="inline-flex items-center gap-1.5 tabular-nums text-foreground">
          <StatusDot className="bg-neutral-400" />
          {counts.queuedRows.toLocaleString()} queued
        </span>
      ),
    });
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground",
        className,
      )}
      aria-label="Scorecard run status counts"
    >
      {items.map(item => (
        <span key={item.key}>{item.node}</span>
      ))}
    </div>
  );
};

export default ScorecardRunStatusCounts;
